import React, { useMemo, useState, useRef, useEffect } from "react";
import { useQuery } from "@apollo/client/react";
import Skeleton from "@mui/material/Skeleton";
import Autocomplete from "@mui/material/Autocomplete";
import Box from "@mui/material/Box";
import Chip from "@mui/material/Chip";
import CheckBoxIcon from "@mui/icons-material/CheckBox";
import CheckBoxOutlineBlankIcon from "@mui/icons-material/CheckBoxOutlineBlank";
import Checkbox from "@mui/material/Checkbox";
import TextField from "@mui/material/TextField";
import ListSubheader from "@mui/material/ListSubheader";
import sortBy from "lodash/sortBy";
import uniq from "lodash/uniq";
import { createFilterOptions } from "@mui/material/Autocomplete";

import { Field } from "formik";
import PopperComponent from "./PopperComponent";
import CustomPaperComponent from "./CustomPaperComponent";
import SelectAllContext from "./SelectAllContext";
import {
  PROJECTS_NAMES_QUERY,
  CLIENTS_AND_PROJECTS_QUERY,
} from "../../../constants/graphql/queries";
import useAuth from "../../../hooks/useAuth";

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;
export const CLIENT_ACCESS_VALUE_PREFIX = "client:";

export const isClientAccessValue = value =>
  typeof value === "string" && value.startsWith(CLIENT_ACCESS_VALUE_PREFIX);

export const clientIdFromAccessValue = value =>
  isClientAccessValue(value) ? value.slice(CLIENT_ACCESS_VALUE_PREFIX.length) : null;

export default function ProjectAutocomplete({
  name,
  required = false,
  multiple = false,
  group = false,
  label,
  emptyLabel = label,
  allProjects = false,
  enableManagedSearch = false,
  managedSearchLabel = null,
  maxSelection = null,
  allowSelectAll = false,
  selectAllDisabled = false,
  onSelectionAction = null,
  scrollToBottomOnOpen = false,
  includeClientAccessOptions = false,
  ...rest
}) {
  const filteredOptionsRef = useRef([]);
  const [inputValue, setInputValue] = useState("");
  const [isOpen, setIsOpen] = useState(false);
  const [includeManagedProjects, setIncludeManagedProjects] = useState(false);
  const [selectionLimitError, setSelectionLimitError] = useState("");
  const { user } = useAuth();
  const { settings } = user.company;
  const shouldIncludeManagedProjects = allProjects || includeManagedProjects;

  const {
    data: projectsData,
    previousData: previousProjectsData,
    loading: projectsLoading,
  } = useQuery(PROJECTS_NAMES_QUERY, {
    variables: {
      manage: shouldIncludeManagedProjects,
    },
    skip: group,
  });
  const {
    data: clientsData,
    previousData: previousClientsData,
    loading: clientsLoading,
  } = useQuery(CLIENTS_AND_PROJECTS_QUERY, {
    variables: {
      manage: shouldIncludeManagedProjects,
    },
    skip: !group,
  });

  const loading = group ? clientsLoading : projectsLoading;
  const projects = group ? [] : projectsData?.projects || previousProjectsData?.projects || [];
  const clients = group ? clientsData?.clients || previousClientsData?.clients || [] : [];
  const showInitialSkeleton = group
    ? loading && !clientsData && !previousClientsData
    : loading && !projectsData && !previousProjectsData;

  const filter = createFilterOptions({
    stringify: (option) => {
      // Include both project name and client name in search
      if (group && option.clientName) {
        return `${option.label} ${option.clientName}`;
      }
      return option.label;
    },
  });
  const customFilterOptions = (options, params) => {
    const filtered = filter(options, params);
    filteredOptionsRef.current = filtered;
    return filtered;
  };

  const filterProject = (project) => shouldIncludeManagedProjects || project.active;
  const getOptionLabel = (project) =>
    !project.active && enableManagedSearch && shouldIncludeManagedProjects
      ? `${project.name} (Disabled)`
      : project.name;
  const showManagedSearchAction = enableManagedSearch && !shouldIncludeManagedProjects;
  const handleManagedSearchClick = () => {
    setIncludeManagedProjects(true);
  };

  const projectsOptions = useMemo(() => {
    if (group) {
      return clients.flatMap((client) => {
        const clientProjects = client.projects.filter(filterProject);

        if (includeClientAccessOptions && clientProjects.length === 0) {
          return [
            {
              label: `Assign Future ${settings.filter_field3}`,
              value: `${CLIENT_ACCESS_VALUE_PREFIX}${client.id}`,
              clientId: client.id,
              clientName: client.name,
            },
          ];
        }

        return clientProjects.map((project) => ({
          label: getOptionLabel(project),
          value: project.id,
          clientId: client.id,
          clientName: client.name,
        }));
      });
    }

    const activeProjects = projects.filter(filterProject);
    const nameCountMap = new Map();

    activeProjects.forEach((project) => {
      const name = project.name;
      nameCountMap.set(name, (nameCountMap.get(name) || 0) + 1);
    });

    const nameInstanceMap = new Map();
    const options = activeProjects.map((project) => {
      const name = project.name;
      const totalCount = nameCountMap.get(name);

      if (totalCount === 1) {
        return {
          label: getOptionLabel(project),
          value: project.id,
        };
      }

      const currentInstance = (nameInstanceMap.get(name) || 0) + 1;
      nameInstanceMap.set(name, currentInstance);

      return {
        label: `${getOptionLabel(project)} (${currentInstance})`,
        value: project.id,
      };
    });

    return sortBy(options, (option) => (option?.label || "").toLowerCase());
  }, [clients, group, includeClientAccessOptions, projects, shouldIncludeManagedProjects]);

  if (showInitialSkeleton) {
    return <Skeleton variant="rounded" width="100%" height={40} />;
  }

  return (
    <Field name={name}>
      {({ field, form, meta }) => {
        const errorText = (meta.touched || form.submitCount > 0) && meta.error ? meta.error : "";

        const hasInitialValues = multiple
          ? field.value === "all" || (field.value && field.value.length > 0)
          : field.value;
        const [showAllProjects, setShowAllProjects] = useState(!hasInitialValues);

        useEffect(() => {
          const hasValues = multiple
            ? field.value === "all" || (field.value && field.value.length > 0)
            : field.value;
          if (!hasValues) {
            setShowAllProjects(true);
          }

          // Clear limit error if selection is now valid
          if (
            maxSelection &&
            multiple &&
            Array.isArray(field.value) &&
            field.value.length <= maxSelection
          ) {
            setSelectionLimitError("");
          }
        }, [field.value, multiple, maxSelection]);

        const selectedValue = multiple
          ? field.value === "all"
            ? projectsOptions
            : projectsOptions.filter((opt) => (field.value || []).includes(opt.value))
          : projectsOptions.find((opt) => opt.value === field.value) || null;

        const hasFieldValues = multiple
          ? field.value === "all" || (field.value && field.value.length > 0)
          : field.value;

        const selectionSummary = useMemo(() => {
          if (!multiple || !hasFieldValues) return null;
          const singularLabel = settings.filter_field2.toLowerCase();
          const pluralLabel = settings.filter_field3.toLowerCase();

          if (field.value === "all") return `All ${pluralLabel} selected`;

          const selectedIds = field.value || [];
          const label = selectedIds.length === 1 ? singularLabel : pluralLabel;

          return `${selectedIds.length} ${label} selected`;
        }, [field.value, hasFieldValues, multiple, settings]);

        const displayOptions = useMemo(() => {
          if (showAllProjects) {
            return projectsOptions;
          }

          if (multiple) {
            if (field.value === "all") {
              return projectsOptions;
            }
            const selectedIds = field.value || [];
            return projectsOptions.filter((opt) => selectedIds.includes(opt.value));
          } else {
            return field.value ? projectsOptions.filter((opt) => opt.value === field.value) : [];
          }
        }, [projectsOptions, showAllProjects, field.value, multiple]);

        const toggleShowAllProjects = () => {
          setShowAllProjects((prev) => !prev);
        };

        const getAllFilteredSelected = () => {
          if (!multiple || filteredOptionsRef.current.length === 0) return false;
          if (field.value === "all") return true;
          return filteredOptionsRef.current.every((opt) => (field.value || []).includes(opt.value));
        };

        const handleSelectAll = () => {
          const wasSelectAllPreviouslySelected = getAllFilteredSelected();
          const isSelectingAllProjects =
            !inputValue && filteredOptionsRef.current.length === projectsOptions.length;

          if (!wasSelectAllPreviouslySelected) {
            // If selecting all projects without any search filter AND allowSelectAll is enabled, use "all" marker for company-wide report
            if (isSelectingAllProjects && allowSelectAll) {
              setSelectionLimitError("");
              form.setFieldValue(field.name, "all");
              if (rest.onChange) rest.onChange("all", filteredOptionsRef.current);
              if (onSelectionAction) {
                onSelectionAction({
                  type: "select_all",
                  value: "all",
                  selectedOptions: filteredOptionsRef.current,
                });
              }
            } else {
              // Otherwise, selecting filtered subset - pass the specific project IDs
              const allFilteredIds = filteredOptionsRef.current.map((opt) => opt.value);
              const currentIds = field.value === "all" ? [] : field.value || [];
              const newIds = [...new Set([...currentIds, ...allFilteredIds])];

              // Check if this would exceed the limit
              if (maxSelection && newIds.length > maxSelection) {
                setSelectionLimitError(`Cannot select more than ${maxSelection} customers.`);
                return;
              }

              setSelectionLimitError("");
              form.setFieldValue(field.name, newIds);
              // Pass both IDs and full options for handleSelectAll
              const selectedOptions = filteredOptionsRef.current;
              if (rest.onChange) rest.onChange(newIds, selectedOptions);
              if (onSelectionAction) {
                onSelectionAction({
                  type: "options_change",
                  value: newIds,
                  selectedOptions,
                });
              }
            }
          } else {
            const filteredIds = filteredOptionsRef.current.map((opt) => opt.value);
            const newIds = inputValue
              ? (field.value === "all" ? [] : field.value || []).filter(
                  (id) => !filteredIds.includes(id)
                )
              : [];

            setSelectionLimitError("");
            form.setFieldValue(field.name, newIds);
            // Pass IDs only for deselection
            if (rest.onChange) rest.onChange(newIds, []);
            if (onSelectionAction) {
              onSelectionAction({
                type: "clear_all",
                value: newIds,
                selectedOptions: [],
              });
            }
          }
        };

        const handleChange = (event, newValue, reason) => {
          if (multiple) {
            if (reason === "clear") {
              setInputValue("");
              return;
            }

            const nextIds = (newValue || []).map(opt => opt.value);
            
            // Check if this would exceed the limit (only for array selections, not "all")
            if (maxSelection && nextIds.length > maxSelection) {
              setSelectionLimitError(`Cannot select more than ${maxSelection} customers.`);
              return;
            }

            // If all projects are selected without search filter, use "all" marker for company-wide report
            const isSelectingAllProjects = nextIds.length === projectsOptions.length && !inputValue;
            const finalValue = isSelectingAllProjects ? "all" : nextIds;

            setSelectionLimitError("");
            form.setFieldValue(field.name, finalValue);
            // Pass both IDs and full options so onChange handler can access clientId
            if (rest.onChange) rest.onChange(finalValue, newValue);
            if (onSelectionAction) {
              onSelectionAction({
                type: "options_change",
                value: finalValue,
                selectedOptions: newValue || [],
              });
            }
          } else {
            const nextValue = newValue ? newValue.value : "";
            form.setFieldValue(field.name, nextValue);
            if (rest.onChange) rest.onChange(newValue);
          }
        };

        const handleInputChange = (event, newInputValue, reason) => {
          if (reason === "input" || reason === "clear") {
            setInputValue(newInputValue);
            if (newInputValue) {
              setShowAllProjects(true);
            } else if (hasFieldValues) {
              setShowAllProjects(false);
            }
          }
        };

        const handleOpen = event => {
          if (rest.onOpen) rest.onOpen(event);
          if (!scrollToBottomOnOpen) return;

          const target = event?.currentTarget;
          requestAnimationFrame(() => {
            const dialog = target?.closest?.('[role="dialog"]');
            const scrollContainer = dialog?.querySelector?.(".MuiDialogContent-root") || dialog;
            scrollContainer?.scrollTo?.({
              top: scrollContainer.scrollHeight,
              behavior: "smooth",
            });
          });
        };

        return (
          <SelectAllContext.Provider
            value={{
              multiple,
              handleSelectAll,
              getAllFilteredSelected,
              filteredOptionsRef,
              showAllProjects,
              toggleShowAllProjects,
              hasFieldValues,
              selectAllDisabled,
              showManagedSearchAction,
              managedSearchLabel,
              handleManagedSearchClick,
            }}
          >
            <Box>
              <Autocomplete
                multiple={multiple}
                disableCloseOnSelect={multiple}
                {...rest}
                options={displayOptions}
                groupBy={group ? option => option.clientId : undefined}
                filterOptions={customFilterOptions}
                getOptionLabel={option => option.label}
                isOptionEqualToValue={(option, value) => option?.value === value?.value}
                value={selectedValue}
                onChange={handleChange}
                onOpen={handleOpen}
                inputValue={inputValue}
                onInputChange={handleInputChange}
                PaperComponent={CustomPaperComponent}
                PopperComponent={PopperComponent}
                ListboxProps={{
                  style: {
                    maxHeight: "300px",
                    overflow: "auto",
                  },
                }}
                renderGroup={
                  group
                    ? params => {
                        const clientId = params.group;
                        const client = clients.find(c => c.id === clientId);
                        const clientName = client ? client.name : clientId;

                        const groupProjectIds = uniq(
                          projectsOptions
                            .filter(opt => opt.clientId === clientId)
                            .map(opt => opt.value)
                        );
                        const selectedIds = field.value === "all" ? projectsOptions.map(opt => opt.value) : (field.value || []);
                        const selectedCount = selectedIds.filter(id => groupProjectIds.includes(id))
                          .length;
                        const allSelected =
                          selectedCount === groupProjectIds.length && groupProjectIds.length > 0;
                        const indeterminate = selectedCount > 0 && !allSelected;

                        const handleGroupCheckboxChange = event => {
                          event.stopPropagation();
                          let newValues;
                          const currentIds = field.value === "all" ? projectsOptions.map(opt => opt.value) : (field.value || []);
                          if (allSelected) {
                            newValues = currentIds.filter(id => !groupProjectIds.includes(id));
                            setSelectionLimitError("");
                            form.setFieldValue(field.name, newValues);
                            if (rest.onChange) rest.onChange(newValues, []);
                            if (onSelectionAction) {
                              onSelectionAction({
                                type: "group_clear",
                                clientId,
                                value: newValues,
                                selectedOptions: [],
                              });
                            }
                          } else {
                            newValues = Array.from(new Set([...currentIds, ...groupProjectIds]));

                            // Check if this would exceed the limit
                            if (maxSelection && newValues.length > maxSelection) {
                              setSelectionLimitError(`Cannot select more than ${maxSelection} customers.`);
                              return;
                            }
                            setSelectionLimitError("");

                            // Get all the option objects for the selected projects
                            const selectedOptions = projectsOptions.filter(opt => newValues.includes(opt.value));
                            form.setFieldValue(field.name, newValues);
                            if (rest.onChange) rest.onChange(newValues, selectedOptions);
                            if (onSelectionAction) {
                              onSelectionAction({
                                type: "group_select",
                                clientId,
                                value: newValues,
                                selectedOptions,
                              });
                            }
                          }
                        };

                        return [
                          <ListSubheader
                            key={`${params.key}-subheader`}
                            component="div"
                            style={{ lineHeight: 1, backgroundColor: "#f4f6f8" }}
                          >
                            {multiple && (
                              <Checkbox
                                icon={icon}
                                checkedIcon={checkedIcon}
                                indeterminate={indeterminate}
                                checked={allSelected}
                                onClick={handleGroupCheckboxChange}
                                style={{ marginRight: 8 }}
                              />
                            )}
                            {clientName}
                          </ListSubheader>,
                          params.children,
                        ];
                      }
                    : undefined
                }
                renderOption={(props, option, { selected }) => {
                  const marginLeft = group ? 24 : 0;
                  return (
                    <li {...props}>
                      {multiple && (
                        <Checkbox
                          icon={icon}
                          checkedIcon={checkedIcon}
                          style={{ marginRight: 8, marginLeft }}
                          checked={selected}
                        />
                      )}
                      {option.label}
                    </li>
                  );
                }}
                renderTags={() =>
                  selectionSummary ? (
                    <Chip
                      size="small"
                      label={selectionSummary}
                      sx={{ height: 22, maxWidth: "100%" }}
                    />
                  ) : null
                }
                renderInput={params => {
                  const hasSelections = multiple
                    ? field.value === "all" || (field.value && field.value.length > 0)
                    : field.value;

                  const textFieldLabel = hasSelections ? label : emptyLabel;
                  const displayError = selectionLimitError || errorText;

                  return (
                    <TextField
                      {...params}
                      label={textFieldLabel}
                      variant="outlined"
                      size="small"
                      required={required}
                      error={!!displayError}
                      helperText={displayError}
                    />
                  );
                }}
              />
            </Box>
          </SelectAllContext.Provider>
        );
      }}
    </Field>
  );
}
